import { createDeviceApi } from "@speakai/common/src/device/api";
import { canRegisterReminderDevice, getReminderReleaseReason } from "@speakai/common/src/device/reminder-lifecycle";
import type { DeviceMeta, DeviceRegistration } from "@speakai/common/src/device/types";
import { DevicePlatform } from "@speakai/shared";
import { STORAGE_KEYS } from "@/config/constants";
import { apiClient } from "@/lib/api/client";
import { getDeviceId } from "@/lib/auth/storage";
import { trackFailure } from "@/lib/integrations/sentry-tracking";
import { safeLocalStorage } from "@/lib/storage/safe-local-storage";
import { isIOS } from "@/lib/utils/ua";
import { discardPushToken, fetchPushToken, isFirebasePushSupported } from "./firebase-messaging";
import { markSignedIn, markSignedOut } from "./session-flag";
import { findMeetingReminderRegistration, registerMeetingReminderWorker, unregisterMeetingReminderWorker } from "./worker-registration";

export const BROWSER_REMINDERS_CHANGED = "speak-meeting-reminders-changed";
const LOCK_NAME = "speak-meeting-reminders";
const UNKNOWN = "Unknown";
const RELEASE_TIMEOUT_MS = 5000;
const ROW_GONE_STATUSES = [403, 404];
const REGISTER_REJECTED_STATUSES = [401, 403];
const APP_ID = "co.speakai.app";
const APP_NAME = "Speak AI Web";
const BROWSER_PATTERNS: ReadonlyArray<readonly [string, RegExp]> = [
  ["Edge", /Edg\/([\d.]+)/],
  ["Opera", /OPR\/([\d.]+)/],
  ["Firefox", /Firefox\/([\d.]+)/],
  ["Chrome", /Chrome\/([\d.]+)/],
  ["Safari", /Version\/([\d.]+).*Safari/],
];

export type BrowserStatus = "unsupported" | "ios-install" | "blocked" | "off" | "on";
export type EnableResult = "enabled" | "unsupported" | "dismissed" | "blocked" | "failed";
type RegisterResult = "registered" | "rejected" | "failed";

// Deliberately no push token here: the token stays inside Firebase and the server row, so local storage leaks nothing usable.
interface BrowserRecord {
  userId: string;
  enabled: true;
}

const deviceApi = createDeviceApi(apiClient);
// Registration happens once per page session; the token is fetched again on the next load.
let registeredThisSession = false;
let lastReleasedEpoch: string | null = null;

function notifyChanged(): void {
  window.dispatchEvent(new Event(BROWSER_REMINDERS_CHANGED));
}

function readRecord(): BrowserRecord | null {
  try {
    const parsed = JSON.parse(safeLocalStorage()?.getItem(STORAGE_KEYS.BROWSER_PUSH) ?? "null");
    if (typeof parsed?.userId !== "string") return null;
    const record: BrowserRecord = { userId: parsed.userId, enabled: true };
    // Rewrite old records that still carry a token so it is dropped from storage.
    if ("token" in parsed) writeRecord(record);
    return record;
  } catch {
    return null;
  }
}

function writeRecord(record: BrowserRecord): boolean {
  try {
    const storage = safeLocalStorage();
    storage?.setItem(STORAGE_KEYS.BROWSER_PUSH, JSON.stringify(record));
    return storage?.getItem(STORAGE_KEYS.BROWSER_PUSH) !== null;
  } catch {
    return false;
  }
}

function readEpoch(): string {
  try {
    return safeLocalStorage()?.getItem(STORAGE_KEYS.BROWSER_PUSH_EPOCH) ?? "0";
  } catch {
    return "0";
  }
}

// The epoch changes on every sign-out or turn-off, so an in-flight enable can tell it was overtaken.
function bumpEpoch(): void {
  try {
    safeLocalStorage()?.setItem(STORAGE_KEYS.BROWSER_PUSH_EPOCH, String(Number(readEpoch()) + 1));
  } catch {
    return;
  }
}

function getPendingDeactivate(): string | null {
  try {
    return safeLocalStorage()?.getItem(STORAGE_KEYS.BROWSER_PUSH_PENDING_DEACTIVATE) || null;
  } catch {
    return null;
  }
}

function setPendingDeactivate(deviceId: string | null): void {
  try {
    const storage = safeLocalStorage();
    if (deviceId) storage?.setItem(STORAGE_KEYS.BROWSER_PUSH_PENDING_DEACTIVATE, deviceId);
    else storage?.removeItem(STORAGE_KEYS.BROWSER_PUSH_PENDING_DEACTIVATE);
  } catch {
    return;
  }
}

function clearRecord(): void {
  try {
    safeLocalStorage()?.removeItem(STORAGE_KEYS.BROWSER_PUSH);
  } catch {
    return;
  }
}

function isInstalledWebApp(): boolean {
  return window.matchMedia("(display-mode: standalone)").matches || (navigator as { standalone?: boolean }).standalone === true;
}

export function getBrowserStatus(userId: string | undefined): BrowserStatus {
  if (isIOS() && !isInstalledWebApp()) return "ios-install";
  if (!("Notification" in window) || !("serviceWorker" in navigator) || !("PushManager" in window)) return "unsupported";
  if (Notification.permission === "denied") return "blocked";
  const optedIn = { optedInUserId: readRecord()?.userId, currentUserId: userId, channelEnabled: true };
  return canRegisterReminderDevice(optedIn) && Notification.permission === "granted" ? "on" : "off";
}

function describeBrowser(): { name: string; version: string } {
  for (const [name, pattern] of BROWSER_PATTERNS) {
    const match = pattern.exec(navigator.userAgent);
    if (match) return { name, version: match[1] };
  }
  return { name: UNKNOWN, version: UNKNOWN };
}

function buildDeviceMeta(): DeviceMeta {
  const browser = describeBrowser();
  const operatingSystem = navigator.platform || UNKNOWN;
  return {
    device: {
      isVirtual: false,
      name: browser.name,
      operatingSystem,
      platform: DevicePlatform.WEB,
      webViewVersion: browser.version,
      osVersion: UNKNOWN,
      model: operatingSystem,
      manufacturer: navigator.vendor || UNKNOWN,
    },
    app: { build: __APP_RELEASE__, version: __APP_VERSION__, id: APP_ID, name: APP_NAME },
  };
}

function buildDeviceRegistration(token: string): DeviceRegistration {
  return { deviceId: getDeviceId(), deviceToken: token, deviceType: DevicePlatform.WEB, meta: buildDeviceMeta() };
}

// Web Lock so two tabs never register or deactivate at the same time.
async function runExclusive<T>(task: () => Promise<T>): Promise<T> {
  return navigator.locks ? ((await navigator.locks.request(LOCK_NAME, task)) as T) : task();
}

async function acquireToken(): Promise<string | null> {
  return fetchPushToken(await registerMeetingReminderWorker());
}

// The signed-in flag is written only if no sign-out happened meanwhile, and rechecked after the write, so the worker never shows for a signed-out user.
async function grantWorkerAccess(userId: string, epoch: string): Promise<boolean> {
  if (readEpoch() !== epoch) return false;
  await markSignedIn(userId).catch(() => undefined);
  if (readEpoch() === epoch) {
    lastReleasedEpoch = null;
    return true;
  }
  await markSignedOut().catch(() => undefined);
  return false;
}

async function registerToken(token: string, userId: string): Promise<RegisterResult> {
  const epoch = readEpoch();
  const res = await deviceApi.register(buildDeviceRegistration(token));
  if (!res.success) {
    const status = res.status ?? 0;
    // The server may have stored the row even though we saw no answer, so remove it.
    if (status === 0 || status >= 500) await deactivateDevice();
    return REGISTER_REJECTED_STATUSES.includes(status) ? "rejected" : "failed";
  }
  if (!(await grantWorkerAccess(userId, epoch))) {
    await deactivateDevice();
    return "failed";
  }
  setPendingDeactivate(null);
  registeredThisSession = true;
  return "registered";
}

// The device id is stored before the call so a failed deactivate is retried on the next sync.
async function deactivateDevice(): Promise<boolean> {
  const deviceId = getPendingDeactivate() ?? getDeviceId();
  setPendingDeactivate(deviceId);
  try {
    const res = await deviceApi.deactivate(deviceId);
    if (res.success || ROW_GONE_STATUSES.includes(res.status ?? 0)) {
      setPendingDeactivate(null);
      return true;
    }
  } catch {
    return false;
  }
  return false;
}

async function revokeSubscription(): Promise<void> {
  const registration = await findMeetingReminderRegistration();
  if (!registration) return;
  // Deleting the Firebase token makes the old token useless even if the server row lingers.
  await discardPushToken(registration).catch(() => undefined);
  await (await registration.pushManager.getSubscription())?.unsubscribe();
}

async function dropLocalSubscription(): Promise<void> {
  registeredThisSession = false;
  await revokeSubscription().catch(() => undefined);
  await unregisterMeetingReminderWorker().catch(() => undefined);
  clearRecord();
  notifyChanged();
}

function reportFailure(action: string, error: unknown): void {
  trackFailure("meeting_assistant", action, { error_reason: error instanceof Error ? error.name : "unknown" });
}

export async function enableOnThisBrowser(userId: string): Promise<EnableResult> {
  const status = getBrowserStatus(userId);
  if (status === "ios-install" || status === "unsupported") return "unsupported";
  const permission = Notification.permission === "default" ? await Notification.requestPermission() : Notification.permission;
  if (permission === "default") return "dismissed";
  if (permission !== "granted") return "blocked";
  return runExclusive(async () => {
    try {
      if (!(await isFirebasePushSupported())) return "unsupported";
      const token = await acquireToken();
      if (!token) return "failed";
      const registered = await registerToken(token, userId);
      if (registered === "rejected") await dropLocalSubscription();
      if (registered !== "registered") return "failed";
      if (!writeRecord({ userId, enabled: true })) {
        await deactivateDevice();
        await dropLocalSubscription();
        return "failed";
      }
      notifyChanged();
      return "enabled";
    } catch (error) {
      reportFailure("reminder_enable_fail", error);
      return "failed";
    }
  });
}

export function turnOffOnThisBrowser(): Promise<void> {
  return runExclusive(async () => {
    if (!readRecord()) return;
    bumpEpoch();
    await deactivateDevice();
    await dropLocalSubscription();
  });
}

// Fail closed on sign-out: clear the worker flag first, bump the epoch, then clear it again to beat a racing enable, and only then touch the network.
export async function releaseBrowserSession(): Promise<void> {
  if (lastReleasedEpoch === readEpoch()) return;
  await markSignedOut().catch(() => undefined);
  bumpEpoch();
  lastReleasedEpoch = readEpoch();
  await markSignedOut().catch(() => undefined);
  registeredThisSession = false;
  if (!readRecord() && !getPendingDeactivate()) return;
  setPendingDeactivate(getPendingDeactivate() ?? getDeviceId());
  // Sign-out must not hang on a slow network; the pending deactivate is retried later.
  const timeout = new Promise<void>((resolve) => setTimeout(resolve, RELEASE_TIMEOUT_MS));
  await Promise.race([runExclusive(deactivateDevice).then(() => undefined), timeout]).catch(() => undefined);
}

export async function syncThisBrowser(userId: string): Promise<void> {
  return runExclusive(async () => {
    if (getPendingDeactivate()) await deactivateDevice();
    const record = readRecord();
    if (!record) return;
    try {
      if (getReminderReleaseReason({ optedInUserId: record.userId, currentUserId: userId, channelEnabled: true }) !== null) {
        await dropLocalSubscription();
        return;
      }
      // The user revoked permission in browser settings.
      if (Notification.permission !== "granted") {
        await deactivateDevice();
        await dropLocalSubscription();
        return;
      }
      if (registeredThisSession) return;
      // Tokens can rotate, so register again once per session.
      const token = await acquireToken();
      if (token) await registerToken(token, userId);
    } catch (error) {
      reportFailure("reminder_sync_fail", error);
    } finally {
      notifyChanged();
    }
  });
}
